import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RotateCcw, Save, Info, Calculator, X } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { PageLoader } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { CalcMethodBadge } from "@/features/finances/components/CalcMethodBadge";
import {
  useNonProjectSmear,
  useSetNonProjectSmear,
  useGroupSmearDetail,
  useSetProjectSuspension,
} from "@/features/finances/hooks/useGroupForecast";

const MONTHS_SHORT = ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"];

const MONTHS_LONG = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

/**
 * Zaokrąglenie udziałów (sumujących się do ~1.0) na liczby całkowite % sumujące się DOKŁADNIE
 * do 100 — metoda największych reszt (Hamilton). Inaczej osobne Math.round dawało 98%.
 */
function roundSharesTo100(items: { id: string; frac: number }[]): Record<string, number> {
  const exact = items.map((it) => ({ id: it.id, e: it.frac * 100 }));
  const target = Math.round(exact.reduce((s, r) => s + r.e, 0)); // ~100 gdy udziały sumują się do 1
  const out: Record<string, number> = {};
  const parts = exact.map((r) => {
    const base = Math.floor(r.e);
    out[r.id] = base;
    return { id: r.id, rem: r.e - base };
  });
  let left = target - parts.reduce((s, p) => s + out[p.id], 0);
  parts.sort((a, b) => b.rem - a.rem);
  for (let i = 0; i < parts.length && left > 0; i++, left--) out[parts[i].id] += 1;
  return out;
}

/**
 * Podział kosztów obsługi firmy (kadry, księgowość — ludzie nieprojektowi) na grupy zleceń.
 * Domyślnie automat (proporcjonalnie do wartości i aktywności grup). CEO może nadpisać ręcznie.
 */
export function NonProjectSmearPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const { data, isLoading } = useNonProjectSmear(year, month);
  const setSmear = useSetNonProjectSmear();
  // grupa otwarta w modalu planowania (klik nazwy grupy)
  const [detailGroup, setDetailGroup] = useState<{ id: string; name: string } | null>(null);

  // wartości edytowane lokalnie (% per grupa). Start: override jeśli jest, inaczej automat.
  const [values, setValues] = useState<Record<string, string>>({});
  // robocza wartość zlecenia per grupa (NIE zapisywana w bazie — tylko do przeliczenia semi-automatu)
  const [valueDraft, setValueDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!data) return;
    // largest-remainder → suma dokładnie 100% (nie 98% jak przy osobnym Math.round)
    const rounded = roundSharesTo100(
      data.groups.map((g) => ({ id: g.group_id, frac: g.override !== null ? g.override : g.auto_share })),
    );
    const init: Record<string, string> = {};
    const vinit: Record<string, string> = {};
    for (const g of data.groups) {
      init[g.group_id] = String(rounded[g.group_id] ?? 0);
      vinit[g.group_id] = g.value.toFixed(2);
    }
    setValues(init);
    setValueDraft(vinit);
  }, [data]);

  // współczynnik aktywności per grupa = waga/wartość (zachowywany przy edycji wartości roboczej)
  const activityOf = useMemo(() => {
    const m: Record<string, number> = {};
    for (const g of data?.groups ?? []) m[g.group_id] = g.value > 0 ? g.weight / g.value : 1;
    return m;
  }, [data]);

  const valueDraftDirty = useMemo(
    () => (data ? data.groups.some((g) => parseFloat(valueDraft[g.group_id] || "0") !== g.value) : false),
    [data, valueDraft],
  );

  // semi-automat: przelicz udziały z (ewentualnie zmienionych) wartości × aktywność → „Ręcznie"
  function recomputeFromValues() {
    if (!data) return;
    const weights = data.groups.map((g) => ({
      id: g.group_id,
      w: (parseFloat(valueDraft[g.group_id] || "0") || 0) * (activityOf[g.group_id] ?? 1),
    }));
    const sum = weights.reduce((s, x) => s + x.w, 0);
    const rounded = roundSharesTo100(weights.map((x) => ({ id: x.id, frac: sum > 0 ? x.w / sum : 0 })));
    const next: Record<string, string> = {};
    for (const g of data.groups) next[g.group_id] = String(rounded[g.group_id] ?? 0);
    setValues(next);
  }

  function shift(d: number) {
    let ny = year, nm = month + d;
    if (nm < 1) { ny--; nm = 12; }
    if (nm > 12) { ny++; nm = 1; }
    setYear(ny); setMonth(nm);
  }

  const total = useMemo(
    () => Object.values(values).reduce((s, v) => s + (parseInt(v || "0", 10) || 0), 0),
    [values]
  );
  // automat jako liczby całkowite sumujące się do 100% (ta sama metoda co inicjalizacja edycji)
  const autoPct = useMemo(
    () => (data ? roundSharesTo100(data.groups.map((g) => ({ id: g.group_id, frac: g.auto_share }))) : {}),
    [data]
  );

  function save() {
    const entries = Object.entries(values)
      .map(([group, v]) => ({ group, fraction: ((parseInt(v || "0", 10) || 0) / 100).toFixed(4) }))
      .filter((e) => parseFloat(e.fraction) > 0);
    setSmear.mutate(entries);
  }
  function resetToAuto() {
    setSmear.mutate([]); // pusta lista = powrót do automatu
  }

  if (isLoading || !data) return <PageLoader />;

  return (
    <div className="flex flex-col gap-4 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">Podział kosztów obsługi firmy</h1>
            <CalcMethodBadge kind="analytical" />
          </div>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
            Koszt ludzi nieprojektowych (kadry, księgowość, zarząd) rozkłada się na grupy zleceń.
            Domyślnie automatycznie — proporcjonalnie do wartości i aktywności grup. Możesz go nadpisać
            ręcznie, jeśli chcesz inaczej obciążyć konkretne zlecenia.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronLeft size={18} /></button>
          <span className="px-3 font-semibold text-sm text-gray-800 dark:text-gray-100 min-w-[140px] text-center">{MONTHS_LONG[month - 1]} {year}</span>
          <button onClick={() => shift(1)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronRight size={18} /></button>
        </div>
      </div>

      <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm ${data.manual_active ? "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300" : "bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300"}`}>
        <Info size={16} />
        {data.manual_active
          ? "Aktywny podział RĘCZNY — automat jest wyłączony, dopóki nie przywrócisz."
          : "Aktywny podział AUTOMATYCZNY (wg wartości i aktywności grup). Edycja poniżej włączy tryb ręczny."}
      </div>

      {data.groups.length === 0 ? (
        <p className="text-sm text-gray-400 py-10 text-center">Brak grup zleceń.</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="px-4 py-3 font-medium">Grupa zleceń</th>
                <th className="px-4 py-3 font-medium text-right w-44">Wartość zlecenia (zł)</th>
                <th className="px-4 py-3 font-medium text-right w-28">Wygaszenie</th>
                <th className="px-4 py-3 font-medium text-right w-32">Automat</th>
                <th className="px-4 py-3 font-medium text-right w-40">Ręcznie (%)</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => {
                const edited = parseFloat(valueDraft[g.group_id] || "0") !== g.value;
                const susp = g.value > 0 ? Math.max(0, Math.min(100, Math.round((1 - g.weight / g.value) * 100))) : null;
                return (
                <tr key={g.group_id} className="border-b border-gray-50 dark:border-gray-700/50">
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => setDetailGroup({ id: g.group_id, name: g.group_name })}
                      className="text-left text-gray-800 dark:text-gray-100 hover:text-[var(--color-primary)] hover:underline"
                      title="Planowanie: daty końca projektów + wygaszenie per miesiąc"
                    >
                      {g.short_name && <span className="font-mono text-xs text-gray-400 mr-2">{g.short_name}</span>}
                      {g.group_name}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <input
                      type="number" min={0} step={1000}
                      value={valueDraft[g.group_id] ?? ""}
                      onChange={(e) => setValueDraft({ ...valueDraft, [g.group_id]: e.target.value })}
                      title="Wartość przyjęta do obliczeń. Można zmienić roboczo (nie zapisuje się w bazie) i przeliczyć semi-automat."
                      className={`w-32 px-2 py-1 border rounded text-right tabular-nums bg-white dark:bg-gray-800 dark:text-gray-100 ${edited ? "border-blue-400 ring-1 ring-blue-200" : "border-gray-300 dark:border-gray-600"}`}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums" title="Wygaszenie grupy = średnia wygaszeń projektów ważona ich wartością. Waga automatu = wartość × (1 − wygaszenie).">
                    {susp === null ? (
                      <span className="text-gray-300 dark:text-gray-600">—</span>
                    ) : (
                      <span className={susp > 0 ? "text-amber-600 dark:text-amber-400" : "text-gray-400"}>{susp}%</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-400">{autoPct[g.group_id] ?? 0}%</td>
                  <td className="px-4 py-2.5 text-right">
                    <input
                      type="number" min={0} max={100} step={5}
                      value={values[g.group_id] ?? ""}
                      onChange={(e) => setValues({ ...values, [g.group_id]: e.target.value })}
                      className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-right bg-white dark:bg-gray-800 dark:text-gray-100"
                    />
                    <span className="text-xs text-gray-400 ml-1">%</span>
                  </td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
                <td className="px-4 py-3">Razem</td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right tabular-nums text-gray-400">100%</td>
                <td className={`px-4 py-3 text-right tabular-nums ${total === 100 ? "text-green-600" : "text-gray-500"}`}>{total}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-gray-400 max-w-lg">
          Wartość zlecenia możesz zmienić <b>roboczo</b> (nie zapisuje się) i przeliczyć semi-automat.
          Udziały nie muszą sumować się do 100% — system znormalizuje je proporcjonalnie.
          Zapisany podział <b>obowiązuje dla wszystkich miesięcy w przód</b> (do następnej zmiany).
          Wygaszenie grupy to średnia wygaszeń projektów ważona ich wartością.
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={recomputeFromValues}
            className={valueDraftDirty ? "border-blue-400 text-blue-700 dark:text-blue-400" : ""}
          >
            <Calculator size={15} className="mr-1" /> Przelicz z wartości
          </Button>
          <Button variant="secondary" onClick={resetToAuto} loading={setSmear.isPending} disabled={!data.manual_active}>
            <RotateCcw size={15} className="mr-1" /> Przywróć automat
          </Button>
          <Button onClick={save} loading={setSmear.isPending}>
            <Save size={15} className="mr-1" /> Zapisz podział ręczny
          </Button>
        </div>
      </div>

      {detailGroup && (
        <GroupPlanningModal
          group={detailGroup}
          year={year}
          month={month}
          onClose={() => setDetailGroup(null)}
        />
      )}
    </div>
  );
}

/* ── Modal planowania grupy: wygaszenie per projekt (pełne miesiące, klik na pasku 0–100) ── */
const ACTIVE_RED = "var(--color-primary)";
const WYG_GREY = "#9ca3af";
const lastDay = (y: number, m: number) => new Date(y, m, 0).getDate();
const pad2 = (n: number) => String(n).padStart(2, "0");

// Klikalny pasek miesięcy: 10 prostokątów po 10%. Dół = aktywne (czerwone), góra = wygaszenie (szare).
// Klik bloku ustawia wygaszenie z krokiem 10%; klik najniższego szarego bloku — zdejmuje (odklik).
const STEP_BLOCKS = 10;
function ProjectMonthStrip({
  months,
  getPct,
  onSet,
}: {
  months: { year: number; month: number; suspension: number | null; editable: boolean }[];
  getPct: (mm: { year: number; month: number }) => number | null;
  onSet: (mm: { year: number; month: number }, pct: number) => void;
}) {
  return (
    <div className="flex gap-1">
      {months.map((mm) => {
        const raw = getPct(mm); // null = projekt nie istniał
        const exists = raw !== null && mm.suspension !== null;
        const g = exists ? Math.round((raw ?? 0) / 10) : 0; // liczba szarych bloków (od góry)
        const clickBlock = (fromTop: number) => {
          if (!mm.editable || !exists) return;
          const candidate = (fromTop + 1) * 10;          // szare kryje górne fromTop+1 bloków
          onSet(mm, candidate === g * 10 ? fromTop * 10 : candidate); // ten sam → odklik o 1
        };
        return (
          <div key={`${mm.year}-${mm.month}`} className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div
              className={`w-full h-20 rounded-md overflow-hidden border flex flex-col gap-px ${
                !exists ? "border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 justify-center" :
                mm.editable ? "border-gray-300 dark:border-gray-600" : "border-gray-200 dark:border-gray-700 opacity-60"
              }`}
              title={!exists ? "Projekt jeszcze nie istniał" : `Wygaszenie ${g * 10}%${mm.editable ? " — kliknij blok (krok 10%)" : " (historia)"}`}
            >
              {!exists ? (
                <span className="text-center text-gray-300 dark:text-gray-600 text-xs">—</span>
              ) : (
                Array.from({ length: STEP_BLOCKS }).map((_, idx) => {
                  const isGrey = idx < g; // idx 0 = góra
                  return (
                    <div
                      key={idx}
                      onClick={() => clickBlock(idx)}
                      className={`flex-1 ${mm.editable ? "cursor-pointer hover:opacity-80" : ""}`}
                      style={{ background: isGrey ? WYG_GREY : ACTIVE_RED }}
                    />
                  );
                })
              )}
            </div>
            <span className="text-[9px] text-gray-400 whitespace-nowrap">
              {MONTHS_SHORT[mm.month - 1]} {String(mm.year).slice(2)}{exists && g > 0 ? ` · ${g * 10}%` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function GroupPlanningModal({
  group,
  year,
  month,
  onClose,
}: {
  group: { id: string; name: string };
  year: number;
  month: number;
  onClose: () => void;
}) {
  const { data, isLoading } = useGroupSmearDetail(group.id, year, month, 12);
  const setSuspension = useSetProjectSuspension();
  // draft[pid]["y-m"] = % wygaszenia (pełny miesiąc); tylko miesiące istniejące
  const [draft, setDraft] = useState<Record<string, Record<string, number>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    const d: Record<string, Record<string, number>> = {};
    for (const p of data.projects) {
      d[p.id] = {};
      for (const mm of p.months) {
        if (mm.suspension !== null) d[p.id][`${mm.year}-${mm.month}`] = mm.suspension;
      }
    }
    setDraft(d);
  }, [data]);

  const origOf = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of data?.projects ?? []) {
      const o: Record<string, number> = {};
      for (const mm of p.months) if (mm.suspension !== null) o[`${mm.year}-${mm.month}`] = mm.suspension;
      m[p.id] = JSON.stringify(o);
    }
    return m;
  }, [data]);

  const changedIds = useMemo(
    () => Object.keys(draft).filter((id) => JSON.stringify(draft[id]) !== (origOf[id] ?? "{}")),
    [draft, origOf],
  );

  // buduje okresy (pełnomiesięczne) z edytowalnych miesięcy projektu, scalając sąsiednie równe %
  function buildPeriods(pid: string) {
    const proj = data?.projects.find((p) => p.id === pid);
    if (!proj) return [];
    const edms = proj.months.filter((m) => m.editable);
    const getp = (m: { year: number; month: number }) => draft[pid]?.[`${m.year}-${m.month}`] ?? 0;
    const periods: { date_from: string; date_to: string | null; fraction: number }[] = [];
    let i = 0;
    while (i < edms.length) {
      const pct = getp(edms[i]);
      if (pct <= 0) { i++; continue; }
      let j = i;
      while (j + 1 < edms.length && getp(edms[j + 1]) === pct) j++;
      const s = edms[i], e = edms[j];
      periods.push({
        date_from: `${s.year}-${pad2(s.month)}-01`,
        date_to: `${e.year}-${pad2(e.month)}-${pad2(lastDay(e.year, e.month))}`,
        fraction: pct / 100,
      });
      i = j + 1;
    }
    return periods;
  }

  async function save() {
    setSaving(true);
    try {
      for (const pid of changedIds) {
        await setSuspension.mutateAsync({ project: pid, periods: buildPeriods(pid) });
      }
    } finally {
      setSaving(false);
    }
  }

  const totalValue = (data?.projects ?? []).reduce((s, p) => s + p.value, 0);
  const curSusp = data?.months[0]?.suspension ?? null;
  const chartData = (data?.months ?? []).map((m) => ({
    label: `${MONTHS_SHORT[m.month - 1]} ${String(m.year).slice(2)}`,
    aktywne: 100 - (m.suspension ?? 0),
    wygaszenie: m.suspension ?? 0,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[68rem] max-w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* nagłówek */}
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">{group.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Wygaszenie pełnomiesięczne per projekt — klik na pasku ustawia % (góra 0%, dół 100%). Zapis globalny.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={20} /></button>
        </div>

        {isLoading || !data ? (
          <div className="py-20 text-center text-sm text-gray-400">Ładowanie…</div>
        ) : (
          <>
            <div className="px-6 py-5 space-y-6 overflow-y-auto">
              {/* KPI */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">Projekty</div>
                  <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{data.projects.length}</div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">Łączna wartość</div>
                  <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{formatAmount(totalValue.toFixed(2))} <span className="text-xs font-normal text-gray-400">PLN</span></div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">Wygaszenie (bieżący mc)</div>
                  <div className={`text-xl font-bold tabular-nums ${curSusp && curSusp > 0 ? "text-amber-600 dark:text-amber-400" : "text-gray-900 dark:text-gray-100"}`}>
                    {curSusp === null ? "—" : `${curSusp}%`}
                  </div>
                </div>
              </div>

              {/* Wygaszenie grupy per miesiąc (wynik) — aktywne (czerwone) na dole + wygaszenie (szare), suma 100% */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500">Wygaszenie grupy per miesiąc (wynik)</h3>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500">
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: ACTIVE_RED }} /> aktywne</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: WYG_GREY }} /> wygaszenie</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" opacity={0.4} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} tickLine={false} />
                    <YAxis domain={[0, 100]} ticks={[0, 50, 100]} unit="%" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => [`${v}%`, n === "aktywne" ? "Aktywne" : "Wygaszenie"]} />
                    <Bar dataKey="aktywne" stackId="a" fill={ACTIVE_RED} />
                    <Bar dataKey="wygaszenie" stackId="a" fill={WYG_GREY} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-gray-400 mt-1">Średnia wygaszeń projektów ważona wartością. Miesiące sprzed powstania projektu nie są liczone; po dacie końca projekt w pełni wygaszony.</p>
              </div>

              {/* Edytor per projekt — pasek miesięcy */}
              <div>
                <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-2">Wygaszenie per projekt (klik na miesiącu)</h3>
                <div className="space-y-3">
                  {data.projects.map((p) => {
                    const isChanged = changedIds.includes(p.id);
                    return (
                      <div key={p.id} className={`rounded-xl border p-3 ${isChanged ? "border-blue-300 dark:border-blue-700" : "border-gray-200 dark:border-gray-700"}`}>
                        <div className="flex items-center justify-between gap-3 mb-2 text-sm">
                          <div className="min-w-0">
                            {p.short_name && <span className="font-mono text-xs text-gray-400 mr-2">{p.short_name}</span>}
                            <span className="text-gray-900 dark:text-gray-100">{p.name}</span>
                          </div>
                          <span className="text-xs text-gray-400 shrink-0">koniec: {formatDate(p.end_date, "brak")} · {formatAmount(p.value.toFixed(2))} zł</span>
                        </div>
                        <ProjectMonthStrip
                          months={p.months}
                          getPct={(mm) => {
                            const orig = p.months.find((x) => x.year === mm.year && x.month === mm.month);
                            if (!orig || orig.suspension === null) return null;
                            return draft[p.id]?.[`${mm.year}-${mm.month}`] ?? orig.suspension;
                          }}
                          onSet={(mm, pct) =>
                            setDraft((d) => ({ ...d, [p.id]: { ...(d[p.id] ?? {}), [`${mm.year}-${mm.month}`]: pct } }))
                          }
                        />
                      </div>
                    );
                  })}
                  {data.projects.length === 0 && (
                    <p className="text-sm text-gray-400 py-6 text-center">Brak projektów w grupie.</p>
                  )}
                </div>
              </div>
            </div>

            {/* stopka */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs text-gray-400">
                Pełne miesiące. Historia (przed bieżącym miesiącem) tylko do podglądu — zmieniasz bieżący i przyszłe.
              </p>
              <div className="flex items-center gap-2">
                {changedIds.length > 0 && <span className="text-xs text-blue-600 dark:text-blue-400">{changedIds.length} {changedIds.length === 1 ? "projekt zmieniony" : "proj. zmienionych"}</span>}
                <Button variant="secondary" onClick={onClose}>Zamknij</Button>
                <Button onClick={save} loading={saving} disabled={changedIds.length === 0}>
                  <Save size={15} className="mr-1" /> Zapisz wygaszenie
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
